import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "alp.access_token";

// Send credentials (cookie) where possible; we ALSO add Bearer header below
// because k8s ingress was rewriting CORS allow-origin to "*", which browsers
// reject when allow-credentials=true. Token-in-localStorage + Authorization
// header is a robust fallback that doesn't rely on cross-origin cookies.
axios.defaults.withCredentials = true;

function getToken() {
  try { return window.localStorage.getItem(TOKEN_KEY) || ""; } catch (_e) { return ""; }
}
function setToken(t) {
  try {
    if (t) window.localStorage.setItem(TOKEN_KEY, t);
    else window.localStorage.removeItem(TOKEN_KEY);
  } catch (_e) { /* ignore */ }
}

// Iter268 · Install nudge — track successful auths so the dashboard
// can show the PWA install nudge banner on the 2nd+ login.
function _bumpLoginCount() {
  try {
    const key = "agentelead.login_count";
    const cur = parseInt(window.localStorage.getItem(key) || "0", 10);
    window.localStorage.setItem(key, String(cur + 1));
    window.localStorage.setItem("agentelead.last_login_at", String(Date.now()));
  } catch (_e) { /* ignore */ }
}

// Attach Authorization: Bearer for every request when we have a token.
axios.interceptors.request.use((config) => {
  const t = getToken();
  if (t) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

const AuthContext = createContext({
  user: null, loading: true,
  login: async () => {}, register: async () => {}, logout: async () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // Iter120 — if we're mid-Google callback (hash has session_id),
    // AuthCallback will exchange it and call applyLogin() — we must
    // not prematurely mark user=false or we'd redirect to /login.
    if (typeof window !== "undefined" &&
        window.location.hash.includes("session_id=")) {
      setLoading(false);
      return;
    }
    if (!getToken()) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data.user || false);
    } catch (_e) {
      setToken(""); // token invalid/expired
      setUser(false);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    // 2FA gate — propagate the challenge so the Login page can render the
    // 6-digit-code step without granting access.
    if (data?.requires_2fa) {
      return { requires_2fa: true, challenge_token: data.challenge_token, partial: data.user };
    }
    if (data?.access_token) setToken(data.access_token);
    setUser(data.user);
    _bumpLoginCount();   // Iter268 · Install nudge trigger
    return data.user;
  };

  const submit2faChallenge = async (challenge_token, code) => {
    const { data } = await axios.post(`${API}/auth/2fa/challenge`, { challenge_token, code });
    if (data?.access_token) {
      setToken(data.access_token);
      // Pull the full user payload now that we hold a real access token.
      const me = await axios.get(`${API}/auth/me`).then((r) => r.data?.user).catch(() => null);
      setUser(me || false);
      _bumpLoginCount();   // Iter268 · Install nudge trigger
      return me;
    }
    throw new Error("No access token returned");
  };

  const register = async (payload) => {
    // Iter314 · Auto-detect UI language from navigator so new users open
    // the app in their native language. Explicit `lang` in payload wins.
    let lang = payload?.lang;
    if (!lang) {
      const nav = (navigator?.language || "en").toLowerCase();
      if (nav.startsWith("pt")) lang = "pt";
      else if (nav.startsWith("es")) lang = "es";
      else lang = "en";
      // Persist immediately so the app loads in the right language once
      // the auth context flips to authenticated.
      try { window.localStorage.setItem("alp.lang", lang); } catch (_e) { /* noop */ }
    }
    const { data } = await axios.post(`${API}/auth/register`, { ...payload, lang });
    if (data?.access_token) setToken(data.access_token);
    setUser(data.user);
    _bumpLoginCount();   // Iter268 · Install nudge trigger
    return data.user;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}); } catch (_e) { /* ignore */ }
    setToken("");
    setUser(false);
  };

  // Used by /auth/callback (Google OAuth) to hydrate context after the
  // backend returned an access_token + user payload.
  const applyLogin = useCallback((u, accessToken) => {
    if (accessToken) setToken(accessToken);
    setUser(u || false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh, applyLogin, submit2faChallenge }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
